// components.stitches.tsx
// Styled components for the OrderTable UI.
// Provides layout, wrappers, and visual styles for table, paginator, search, and other UI elements in the OrderTable component.

import { styled } from "@stitches/react";

/**
 * Main wrapper for the OrderTable, provides scroll and margin.
 */
export const Wrapper = styled("div", {
  overflow: "auto",
  position: "relative",
  marginTop: "36px",
});

/**
 * Wrapper for paginator and refetch controls, positioned at the top right.
 */
export const PaginatorWrapper = styled("div", {
  position: "absolute",
  boxSizing: "border-box",
  top: 0,
  right: 0,
  zIndex: 1,
  display: "flex",
  gap: 10,
  backgroundColor: "#fff",
  padding: "0 0 10px 10px",
});

/**
 * Form wrapper for the patient name search input.
 */
export const SearchWrapper = styled("form", {
  width: 250,
  display: "flex",
});

/**
 * Container for search and filter interaction buttons.
 */
export const SearchInteractionsContainer = styled("div", {
  display: "flex",
  flexWrap: "wrap",
  gap: 10,
  marginBottom: 10,

  "& button": {
    "&:hover": {
      backgroundColor: "#F0F9FF !important",
      color: "#0466B4 !important",
      "& svg": {
        fill: "#0466B4 !important",
      },
    },
  },
});

/**
 * Wrapper for the main table, handles sticky headers and row highlighting.
 */
export const TableWrapper = styled("div", {
  overflowX: "auto",
  maxHeight: "calc(100vh - 300px)",
  "& .HighlightCell": {
    backgroundColor: "#F0F9FF",
  },
  "& table": {
    "& thead": {
      boxShadow: "2px 2px 4px rgba(0, 0, 0, 0.2)",
      position: "sticky",
      top: 0,
      zIndex: 2,
      "& th": {
        backgroundColor: "#fff",
      },
    },
    "& tbody": {
      "& tr:nth-child(even)": {
        backgroundColor: "#F0EEF2",
        "& .HighlightCell": {
          backgroundColor: "#E2EAF0",
        },
      },
      "& tr": {
        "&:hover": {
          "& td": {
            position: "relative",
            "&::after": {
              content: "",
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.05)",
              zIndex: 1,
              pointerEvents: "none",
            },
          },
          "& .HighlightCell": {
            backgroundColor: "#E1F1FF",
            "&::after": {
              content: "none",
            },
          },
        },
      },
      "& tr:nth-child(even):hover": {
        "& .HighlightCell": {
          backgroundColor: "#D5DEE6",
        },
      },
    },
  },
});

/**
 * Container for refetch button and last updated text.
 */
export const RefetchContainer = styled("div", {
  marginLeft: "auto",
  display: "flex",
  alignItems: "center",
  gap: 10,

  "& button": {
    borderRadius: "50% !important",
  },
});

/**
 * Container for displaying the last updated timestamp.
 */
export const LastUpdatedContainer = styled("div", {
  fontStyle: "italic !important",
});

/**
 * Spacer for visual separation in the paginator row.
 */
export const Spacer = styled("div", {
  borderRight: "1px solid #B6B7B7",
});
